import * as Sentry from "@sentry/react";
import supabase from "@/lib/__shared__/supabase/client";
import { appStore } from "./stores/appStore";

export type ResetPasswordOptions = {
	newPassword: string;
	isOldAuth?: boolean;
	challengeToken?: string;
	resetToken?: string | null;
};

export interface IAuthManager {
	appStore: typeof appStore;
	initialize: () => Promise<void>;
	addToNewsletter: (email: string) => Promise<void>;
	deleteAccount: (_id: string, _accessToken: string) => Promise<void>;
	requestPasswordReset: (email: string, captchaToken?: string) => Promise<void>;
	resetEmail: (email: string) => Promise<void>;
	resetPassword: (options: ResetPasswordOptions) => Promise<void>;
	signInWithEmail: (
		email: string,
		password: string,
		challengeToken: string,
	) => Promise<boolean>;
	signOut: () => Promise<void>;
	signUpWithEmail: (
		email: string,
		password: string,
		captchaToken: string,
	) => Promise<void>;
}

// Frontend expects Errors to be thrown with a message property

export const AuthManager: IAuthManager = {
	appStore: appStore,
	async initialize() {
		const set = appStore.getState();

		set.setAuthLoading(true);

		const { data: sessionData, error: sessionError } =
			await supabase.auth.getSession();

		if (sessionError) {
			set.setAuthLoading(false);
			Sentry.captureException(sessionError);
			throw new Error(sessionError.message);
		}

		set.setSession(sessionData.session);
		set.setAuthLoading(false);

		// Subscribe to future changes
		supabase.auth.onAuthStateChange((_event, session) => {
			set.setSession(session);
		});
	},
	async addToNewsletter(email: string): Promise<void> {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/user/subscribe-newsletter`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					body: JSON.stringify({ email }),
				},
			);

			if (response.status === 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			const json: { success: boolean; message: string } = await response.json();

			if (!json.success) {
				throw new Error(json.message ?? "Failed to subscribe to newsletter.");
			}
		} catch (error) {
			return Promise.resolve(); // Silently fail, we'll catch this later manually API will log it
		}
	},
	async deleteAccount(_id: string, _accessToken: string): Promise<void> {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/user/${_id}`,
				{
					method: "DELETE",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
						Authorization: `Bearer ${_accessToken}`,
					},
				},
			);

			const json: { data: object; message: string } = await response.json();

			if (response.ok) {
				return;
			}

			throw new Error(json.message || "Failed to delete account.");
		} catch (error) {
			console.error(`Error: ${error}`);
			Sentry.captureException(error);
			return Promise.reject(
				error instanceof Error
					? error.message
					: "An unexpected error occurred.",
			);
		}
	},
	// this needs to go to the user server to check if the user is in the old or new database
	async requestPasswordReset(
		email: string,
		captchaToken?: string,
	): Promise<void> {
		const set = appStore.getState();
		try {
			set.setAuthLoading(true);

			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/auth/forgot-password`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					credentials: "include", // Important to include cookies
					body: JSON.stringify({
						email,
						challengeToken: captchaToken,
					}),
				},
			);

			if (response.status === 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			const json: {
				success: boolean;
				data: { accessToken: string; refreshToken: string };
				message: string;
			} = await response.json();

			if (!json.success) {
				set.setAuthLoading(false);
				throw new Error(json.message ?? "Failed to send reset email.");
			}
		} catch (error) {
			return Promise.reject(error);
		} finally {
			set.setAuthLoading(false);
		}
	},
	async resetEmail(email: string): Promise<void> {
		const set = appStore.getState();
		set.setAuthLoading(true);
		const { error } = await supabase.auth.updateUser({
			email,
		});
		if (error) {
			set.setAuthLoading(false);
			Sentry.captureException(error);
			throw new Error(error.message);
		}
		set.setAuthLoading(false);
	},
	async resetPassword(options: ResetPasswordOptions): Promise<void> {
		const set = appStore.getState();
		try {
			const { newPassword, isOldAuth, challengeToken, resetToken } = options;
			set.setAuthLoading(true);

			if (isOldAuth) {
				const response = await fetch(
					`${import.meta.env.VITE_USER_SERVER_URL}/auth/reset-password`,
					{
						method: "POST",
						headers: {
							"Content-type": "application/json; charset=UTF-8",
						},
						credentials: "include", // Important to include cookies
						body: JSON.stringify({
							password: newPassword,
							token: resetToken,
							challengeToken,
						}),
					},
				);

				if (response.status === 500) {
					throw new Error(
						"We apologize, but an error occurred. Please try again later.",
					);
				}

				const json: {
					success: boolean;
					data: { accessToken: string; refreshToken: string };
					message: string;
				} = await response.json();

				if (!json.success) {
					set.setAuthLoading(false);
					throw new Error(json.message ?? "Failed to sign in.");
				}

				const { data: sessionData, error: sessionError } =
					await supabase.auth.setSession({
						access_token: json.data.accessToken,
						refresh_token: json.data.refreshToken,
					});

				if (sessionError) {
					set.setAuthLoading(false);
					Sentry.captureException(sessionError);
					throw new Error(sessionError.message);
				}

				set.setSession(sessionData.session);
			} else {
				const { error } = await supabase.auth.updateUser({
					password: newPassword,
				});

				if (error) {
					set.setAuthLoading(false);
					Sentry.captureException(error);
					throw new Error(error.message);
				}
			}
		} catch (error) {
			return Promise.reject(error);
		} finally {
			set.setAuthLoading(false);
		}
	},
	// Returns true if the user doesn't need to verify their email or false if they do, otherwise error
	async signInWithEmail(
		email: string,
		password: string,
		challengeToken: string,
	): Promise<boolean> {
		const set = appStore.getState();

		try {
			set.setAuthLoading(true);

			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/auth/sign-in`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					credentials: "include", // Important to include cookies
					body: JSON.stringify({
						email,
						password,
						challengeToken,
					}),
				},
			);

			if (response.status >= 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			console.log("Response status:", response.status); // Debug log

			if (response.status === 403) {
				// TODO: we should probably create an AppError class that extends Error and has a status property
				throw new Error("VERIFICATION_REQUIRED");
			}

			const json: {
				success: boolean;
				data: {
					accessToken: string;
					refreshToken: string;
					isAccountVerified: boolean;
				};
				message: string;
			} = await response.json();

			if (!json.success) {
				set.setAuthLoading(false);
				throw new Error(json.message ?? "Failed to sign in.");
			}

			if (json.data.isAccountVerified) {
				const { data: sessionData, error: sessionError } =
					await supabase.auth.setSession({
						access_token: json.data.accessToken,
						refresh_token: json.data.refreshToken,
					});

				if (sessionError) {
					set.setAuthLoading(false);
					Sentry.captureException(sessionError);
					throw new Error(sessionError.message);
				}

				set.setSession(sessionData.session);
				return true; // User doesn't need to verify their email
			}

			return false; // User needs to verify their email
		} catch (error) {
			return Promise.reject(error);
		} finally {
			set.setAuthLoading(false);
		}
	},
	async signOut() {
		const set = appStore.getState();

		set.setAuthLoading(true);

		await supabase.auth.signOut();

		set.setSession(null);
		set.setSubscription(null);
		set.setAuthLoading(false);
	},
	async signUpWithEmail(
		email: string,
		password: string,
		challengeToken: string,
	): Promise<void> {
		const set = appStore.getState();

		try {
			set.setAuthLoading(true);

			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/auth/sign-up`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					credentials: "include", // Important to include cookies
					body: JSON.stringify({
						email,
						password,
						challengeToken,
					}),
				},
			);

			if (response.status >= 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			const json: {
				success: boolean;
				message: string;
			} = await response.json();

			if (!json.success) {
				throw new Error(json.message ?? "Failed to sign in.");
			}

			return;
		} catch (error) {
			return Promise.reject(error);
		} finally {
			set.setAuthLoading(false);
		}
	},
};
